import * as Sentry from '@sentry/react';
import {
  LogLevel,
  LogEntry,
  LoggerConfig,
  GraphQLLogEntry,
  NavigationLogEntry,
  RestLogEntry,
} from './types';
import { serializeForSentry } from './serializeForSentry';

/**
 * Type for Sentry's experimental logger API.
 * Available in Sentry SDK 9.41.0+ with _experiments.enableLogs enabled.
 */
interface SentryLogger {
  trace: (message: string, attributes?: Record<string, unknown>) => void;
  debug: (message: string, attributes?: Record<string, unknown>) => void;
  info: (message: string, attributes?: Record<string, unknown>) => void;
  warn: (message: string, attributes?: Record<string, unknown>) => void;
  error: (message: string, attributes?: Record<string, unknown>) => void;
  fatal: (message: string, attributes?: Record<string, unknown>) => void;
}

/**
 * Extended Sentry type that includes the experimental logger API.
 */
type SentryWithLogger = typeof Sentry & {
  logger?: SentryLogger;
};

/**
 * Get the Sentry logger if available.
 * Returns undefined if not available (SDK < 9.41.0 or enableLogs not set).
 */
function getSentryLogger(): SentryLogger | undefined {
  const sentryWithLogger = Sentry as SentryWithLogger;
  return sentryWithLogger.logger;
}

/**
 * Logger service singleton that integrates with Sentry Logs and console.
 *
 * Behavior based on debug mode:
 * - Debug OFF: Only errors are sent to Sentry (console silent)
 * - Debug ON: All logs are sent to both console AND Sentry
 */
class LoggerService {
  // eslint-disable-next-line no-use-before-define
  private static instance: LoggerService;

  private isDebugModeEnabled = false;

  private userId: string | null = null;

  private userEmail: string | null = null;

  // eslint-disable-next-line no-useless-constructor, no-empty-function
  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Get the singleton instance of LoggerService.
   */
  public static getInstance(): LoggerService {
    if (!LoggerService.instance) {
      LoggerService.instance = new LoggerService();
    }
    return LoggerService.instance;
  }

  /**
   * Configure the logger with debug mode state and user information.
   */
  public configure(config: LoggerConfig): void {
    this.isDebugModeEnabled = config.isDebugModeEnabled;
    this.userId = config.userId ?? null;
    this.userEmail = config.userEmail ?? null;
  }

  /**
   * Determines if a log entry at the given level should be processed.
   * Errors are always logged; other levels only when debug mode is enabled.
   */
  private shouldLog(level: LogLevel): boolean {
    if (level === LogLevel.Error || level === LogLevel.Fatal) {
      return true;
    }
    return this.isDebugModeEnabled;
  }

  /**
   * Get common attributes to include with all log entries.
   */
  private getCommonAttributes(): Record<string, unknown> {
    const attributes: Record<string, unknown> = {};

    if (this.userId) {
      attributes['user.id'] = this.userId;
    }
    if (this.userEmail) {
      attributes['user.email'] = this.userEmail;
    }

    return attributes;
  }

  /**
   * Send log to Sentry using the appropriate log level method.
   * Falls back to Sentry.addBreadcrumb for SDK versions without logger API.
   */
  private sendToSentry(
    level: LogLevel,
    message: string,
    attributes: Record<string, unknown>,
  ): void {
    const sentryLogger = getSentryLogger();
    const allAttributes = { ...this.getCommonAttributes(), ...attributes };

    // If Sentry logger API is available (SDK 9.12.0+), use it
    if (sentryLogger) {
      switch (level) {
        case LogLevel.Trace:
          sentryLogger.trace(message, allAttributes);
          break;
        case LogLevel.Debug:
          sentryLogger.debug(message, allAttributes);
          break;
        case LogLevel.Info:
          sentryLogger.info(message, allAttributes);
          break;
        case LogLevel.Warn:
          sentryLogger.warn(message, allAttributes);
          break;
        case LogLevel.Error:
          sentryLogger.error(message, allAttributes);
          break;
        case LogLevel.Fatal:
          sentryLogger.fatal(message, allAttributes);
          break;
        default:
          sentryLogger.info(message, allAttributes);
      }
      return;
    }

    // Fallback for older SDK versions: use breadcrumbs for non-errors,
    // captureMessage for errors
    if (level === LogLevel.Error || level === LogLevel.Fatal) {
      Sentry.captureMessage(message, {
        level: level === LogLevel.Fatal ? 'fatal' : 'error',
        extra: allAttributes,
      });
    } else {
      // Add as breadcrumb for context in future error reports
      Sentry.addBreadcrumb({
        category: 'debug-log',
        message,
        level: level as Sentry.SeverityLevel,
        data: allAttributes,
      });
    }
  }

  /**
   * Log to console with appropriate styling based on level.
   */
  // eslint-disable-next-line class-methods-use-this
  private logToConsole(
    level: LogLevel,
    message: string,
    attributes?: Record<string, unknown>,
  ): void {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    switch (level) {
      case LogLevel.Trace:
        // eslint-disable-next-line no-console
        console.trace(prefix, message, attributes);
        break;
      case LogLevel.Debug:
        // eslint-disable-next-line no-console
        console.debug(prefix, message, attributes);
        break;
      case LogLevel.Info:
        // eslint-disable-next-line no-console
        console.info(prefix, message, attributes);
        break;
      case LogLevel.Warn:
        // eslint-disable-next-line no-console
        console.warn(prefix, message, attributes);
        break;
      case LogLevel.Error:
      case LogLevel.Fatal:
        // eslint-disable-next-line no-console
        console.error(prefix, message, attributes);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log(prefix, message, attributes);
    }
  }

  /**
   * Internal log method that handles routing to Sentry and console.
   */
  private log(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) {
      return;
    }

    const attributes = entry.attributes || {};

    // Always send to Sentry when shouldLog returns true
    this.sendToSentry(entry.level, entry.message, attributes);

    // Only log to console when debug mode is enabled
    if (this.isDebugModeEnabled) {
      this.logToConsole(entry.level, entry.message, attributes);
    }
  }

  // Public logging methods

  public trace(message: string, attributes?: Record<string, unknown>): void {
    this.log({ level: LogLevel.Trace, message, attributes });
  }

  public debug(message: string, attributes?: Record<string, unknown>): void {
    this.log({ level: LogLevel.Debug, message, attributes });
  }

  public info(message: string, attributes?: Record<string, unknown>): void {
    this.log({ level: LogLevel.Info, message, attributes });
  }

  public warn(message: string, attributes?: Record<string, unknown>): void {
    this.log({ level: LogLevel.Warn, message, attributes });
  }

  public error(message: string, attributes?: Record<string, unknown>): void {
    this.log({ level: LogLevel.Error, message, attributes });
  }

  public fatal(message: string, attributes?: Record<string, unknown>): void {
    this.log({ level: LogLevel.Fatal, message, attributes });
  }

  /**
   * Log a GraphQL operation with detailed information.
   * Uses grouped console output for better readability.
   */
  public logGraphQL(entry: GraphQLLogEntry): void {
    const level = entry.error ? LogLevel.Error : LogLevel.Debug;

    if (!this.shouldLog(level)) {
      return;
    }

    const message = entry.error
      ? `GraphQL ${entry.operationType} ${entry.operationName} failed`
      : `GraphQL ${entry.operationType} ${entry.operationName}`;

    const attributes: Record<string, unknown> = {
      'graphql.operation_name': entry.operationName,
      'graphql.operation_type': entry.operationType,
      'graphql.duration_ms': entry.durationMs,
      ...this.getCommonAttributes(),
    };

    if (entry.variables && Object.keys(entry.variables).length > 0) {
      attributes['graphql.variables'] = serializeForSentry(entry.variables);
    }

    if (entry.response) {
      attributes['graphql.response'] = serializeForSentry(entry.response);
    }

    if (entry.error) {
      attributes['graphql.error'] = entry.error.message;
    }

    // Send to Sentry
    this.sendToSentry(level, message, attributes);

    // Console output with grouping when debug mode is enabled
    if (this.isDebugModeEnabled) {
      const timestamp = new Date().toISOString();
      const groupLabel = `[${timestamp}] GraphQL ${entry.operationType}: ${entry.operationName} (${entry.durationMs}ms)`;

      if (entry.error) {
        // eslint-disable-next-line no-console
        console.groupCollapsed(`%c${groupLabel}`, 'color: #ff6b6b');
      } else {
        // eslint-disable-next-line no-console
        console.groupCollapsed(`%c${groupLabel}`, 'color: #4ecdc4');
      }

      // eslint-disable-next-line no-console
      console.log('Operation:', entry.operationName);
      // eslint-disable-next-line no-console
      console.log('Type:', entry.operationType);
      // eslint-disable-next-line no-console
      console.log('Duration:', `${entry.durationMs}ms`);

      if (entry.variables && Object.keys(entry.variables).length > 0) {
        // eslint-disable-next-line no-console
        console.log('Variables:', entry.variables);
      }

      if (entry.response) {
        // eslint-disable-next-line no-console
        console.log('Response:', entry.response);
      }

      if (entry.error) {
        // eslint-disable-next-line no-console
        console.error('Error:', entry.error);
      }

      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  /**
   * Log a navigation/route change event.
   */
  public logNavigation(entry: NavigationLogEntry): void {
    if (!this.shouldLog(LogLevel.Debug)) {
      return;
    }

    const message = entry.previousPathname
      ? `Navigation: ${entry.previousPathname} → ${entry.pathname}`
      : `Navigation: ${entry.pathname}`;

    const attributes: Record<string, unknown> = {
      'navigation.pathname': entry.pathname,
      ...this.getCommonAttributes(),
    };

    if (entry.search) {
      attributes['navigation.search'] = entry.search;
    }

    if (entry.previousPathname) {
      attributes['navigation.previous_pathname'] = entry.previousPathname;
    }

    // Send to Sentry
    this.sendToSentry(LogLevel.Debug, message, attributes);

    // Console output when debug mode is enabled
    if (this.isDebugModeEnabled) {
      this.logToConsole(LogLevel.Debug, message, {
        pathname: entry.pathname,
        search: entry.search,
        previousPathname: entry.previousPathname,
      });
    }
  }

  /**
   * Log a REST API call with detailed information.
   * Uses grouped console output for better readability.
   */
  public logRest(entry: RestLogEntry): void {
    const level = entry.error ? LogLevel.Error : LogLevel.Debug;

    if (!this.shouldLog(level)) {
      return;
    }

    const message = entry.error
      ? `REST ${entry.method} ${entry.url} failed`
      : `REST ${entry.method} ${entry.url}`;

    const attributes: Record<string, unknown> = {
      'http.method': entry.method,
      'http.url': entry.url,
      'http.duration_ms': entry.durationMs,
      ...this.getCommonAttributes(),
    };

    if (entry.statusCode !== undefined) {
      attributes['http.status_code'] = entry.statusCode;
    }

    if (entry.requestBody) {
      attributes['http.request_body'] = serializeForSentry(entry.requestBody);
    }

    if (entry.responseBody) {
      attributes['http.response_body'] = serializeForSentry(entry.responseBody);
    }

    if (entry.error) {
      attributes['http.error'] = entry.error.message;
    }

    // Send to Sentry
    this.sendToSentry(level, message, attributes);

    // Console output with grouping when debug mode is enabled
    if (this.isDebugModeEnabled) {
      const timestamp = new Date().toISOString();
      const statusText = entry.statusCode ? ` [${entry.statusCode}]` : '';
      const groupLabel = `[${timestamp}] REST ${entry.method} ${entry.url}${statusText} (${entry.durationMs}ms)`;

      if (entry.error) {
        // eslint-disable-next-line no-console
        console.groupCollapsed(`%c${groupLabel}`, 'color: #ff6b6b');
      } else {
        // eslint-disable-next-line no-console
        console.groupCollapsed(`%c${groupLabel}`, 'color: #45b7d1');
      }

      // eslint-disable-next-line no-console
      console.log('Method:', entry.method);
      // eslint-disable-next-line no-console
      console.log('URL:', entry.url);
      // eslint-disable-next-line no-console
      console.log('Duration:', `${entry.durationMs}ms`);

      if (entry.statusCode !== undefined) {
        // eslint-disable-next-line no-console
        console.log('Status:', entry.statusCode);
      }

      if (entry.requestBody) {
        // eslint-disable-next-line no-console
        console.log('Request Body:', entry.requestBody);
      }

      if (entry.responseBody) {
        // eslint-disable-next-line no-console
        console.log('Response Body:', entry.responseBody);
      }

      if (entry.error) {
        // eslint-disable-next-line no-console
        console.error('Error:', entry.error);
      }

      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  /**
   * Check if debug mode is currently enabled.
   */
  public isDebugMode(): boolean {
    return this.isDebugModeEnabled;
  }
}

// Export singleton instance
export const logger = LoggerService.getInstance();

// Export class for testing purposes
export { LoggerService };
